import { createI18n } from 'vue-i18n';

import de from './de.json';
import en from './en.json';

export const LOCALES = ['de', 'en'] as const;
export type AppLocale = (typeof LOCALES)[number];

export const LOCALE_STORAGE_KEY = 'oamtc.locale';

export function isAppLocale(value: unknown): value is AppLocale {
  return value === 'de' || value === 'en';
}

export function readStoredLocale(): AppLocale {
  if (typeof localStorage === 'undefined') return 'de';
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  return isAppLocale(stored) ? stored : 'de';
}

export const i18n = createI18n({
  legacy: false,
  locale: readStoredLocale(),
  fallbackLocale: 'en',
  missingWarn: false,
  fallbackWarn: false,
  messages: { de, en },
});

export function applyLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

applyLocale(readStoredLocale());

/** Atom captions keyed by id. Falls back to the English registry label. */
export function atomLabel(id: string, fallback: string): string {
  void i18n.global.locale.value;
  const key = `atoms.${id}`;
  return i18n.global.te(key) ? String(i18n.global.t(key)) : fallback;
}

/** Titles keyed by engine id (`G1`, `G1.a`, `I1.a`). Dots in the id are literal keys. */
export function ruleTitle(id: string, fallback: string): string {
  void i18n.global.locale.value;
  const titles = i18n.global.tm('ruleTitles');
  if (titles && typeof titles === 'object' && id in titles) {
    const value = (titles as Record<string, unknown>)[id];
    if (typeof value === 'string') return value;
  }
  return fallback;
}

export type DetailParamValue = string | number | readonly string[];
export type DetailParams = Record<string, DetailParamValue>;

const LABEL_MAP: Record<string, string> = {
  labels: 'engineDetails.timeline',
  missing: 'engineDetails.missingDates',
};

function localizeLabel(token: string, map: string): string {
  const key = `${map}.${token}`;
  return i18n.global.te(key) ? String(i18n.global.t(key)) : token;
}

function flattenParams(params: DetailParams | undefined): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  const labels = params?.labels;
  const dates = params?.dates;
  if (Array.isArray(labels) && Array.isArray(dates)) {
    out.order = labels
      .map((label, index) => `${localizeLabel(String(label), LABEL_MAP.labels)} ${dates[index] ?? ''}`.trim())
      .join(', ');
  }

  for (const [key, value] of Object.entries(params ?? {})) {
    if (key === 'labels' || key === 'dates') continue;
    if (typeof value === 'string' || typeof value === 'number') {
      out[key] = value;
      continue;
    }
    const map = LABEL_MAP[key];
    out[key] = value.map((item) => (map ? localizeLabel(item, map) : item)).join(', ');
  }
  return out;
}

/** Required-document names keyed by completeness id. */
export function requiredDocTitle(id: string, fallback = id): string {
  void i18n.global.locale.value;
  const key = `requiredDocs.${id}`;
  return i18n.global.te(key) ? String(i18n.global.t(key)) : fallback;
}

/** Translate an inspector detail. Falls back to the stored English engine sentence. */
export function engineDetail(
  key: string | undefined,
  params: DetailParams | undefined,
  fallback: string,
): string {
  void i18n.global.locale.value;
  if (!key || !i18n.global.te(key)) return fallback;
  return String(i18n.global.t(key, flattenParams(params)));
}

export type EchoState = 'value' | 'notFound' | 'unreadable' | 'conflict';

/** Inspector echo of an atom value. Slot states follow the Facts-tab labels. */
export function echoValue(state: EchoState, value: unknown): string {
  void i18n.global.locale.value;
  if (state === 'notFound') return String(i18n.global.t('atom.notFound'));
  if (state === 'unreadable') return String(i18n.global.t('atom.unreadable'));
  const formatRaw = (item: unknown): string => {
    if (item === true) return String(i18n.global.t('atom.yes'));
    if (item === false) return String(i18n.global.t('atom.no'));
    if (item === null || item === undefined) return '—';
    return String(item);
  };
  if (state === 'conflict') {
    const values = Array.isArray(value) ? value : [value];
    return values.map(formatRaw).join(' / ');
  }
  return formatRaw(value);
}
